import React, {createContext, useContext, useMemo} from 'react';
import type {DocsetManifest, DocNode} from '../types/docset';
import {buildNodeIndex} from '../utils/nodeIndex';
import {attachParentReferences} from '../utils/navigation';
import type {NodeIndex} from '../utils/nodeIndex';

type DocsetContextValue = {
  manifest: DocsetManifest;
  nodeIndex: NodeIndex;
};

const DocsetContext = createContext<DocsetContextValue | undefined>(undefined);

type DocsetProviderProps = {
  manifest: DocsetManifest;
  children: React.ReactNode;
};

export function DocsetProvider({manifest, children}: DocsetProviderProps) {
  const nodeIndex = useMemo(() => {
    attachParentReferences(manifest.nav);
    return buildNodeIndex(manifest.nav);
  }, [manifest]);

  const value = useMemo<DocsetContextValue>(
    () => ({
      manifest,
      nodeIndex,
    }),
    [manifest, nodeIndex],
  );

  return <DocsetContext.Provider value={value}>{children}</DocsetContext.Provider>;
}

export function useDocset(): DocsetContextValue {
  const ctx = useContext(DocsetContext);
  if (!ctx) {
    throw new Error('useDocset must be used within DocsetProvider');
  }
  return ctx;
}

export function useDocsetManifest(): DocsetManifest {
  return useDocset().manifest;
}

export function useNodeIndex(): NodeIndex {
  return useDocset().nodeIndex;
}

export function useNodeByPath(pathSegments: string[]): DocNode | undefined {
  const {nodeIndex} = useDocset();
  return nodeIndex.byPath.get(pathSegments.join('/'));
}
